import { makeAutoObservable, runInAction } from 'mobx';
import axios from 'axios';
import {
  adminAuthSendCode,
  adminAuthLogin,
  OpenAPI,
  AdminSendCodeDto,
} from '@pro-sto/api-client';
import type { AdminUserDto } from '@pro-sto/api-client';
import { toastStore } from './ToastStore';

const API_BASE = () => OpenAPI.BASE || '';

interface RefreshResponse {
  accessToken: string;
  refreshToken: string;
}

export class AuthStore {
  isAuthenticated = false;
  accessToken: string | null = null;
  refreshToken: string | null = null;
  user: AdminUserDto | null = null;
  isLoading = false;

  constructor() {
    makeAutoObservable(this);
    this.checkAuth();
  }

  checkAuth = () => {
    const accessToken = localStorage.getItem('accessToken');
    const refreshToken = localStorage.getItem('refreshToken');
    const userStr = localStorage.getItem('user');

    if (accessToken && refreshToken && userStr) {
      try {
        const user = JSON.parse(userStr) as AdminUserDto;

        if (user.auth_panel === AdminSendCodeDto.panel.SERVICE_CENTER) {
          this.logout();
          return;
        }

        runInAction(() => {
          this.accessToken = accessToken;
          this.refreshToken = refreshToken;
          this.user = user;
          this.isAuthenticated = true;
        });
        OpenAPI.TOKEN = accessToken;
      } catch (error) {
        console.error('Ошибка при парсинге данных пользователя:', error);
        this.logout();
      }
    }
  };

  sendCode = async (phone: string): Promise<void> => {
    this.isLoading = true;
    try {
      await adminAuthSendCode({
        requestBody: {
          phone,
          panel: AdminSendCodeDto.panel.SUPERADMIN,
        },
      });
      runInAction(() => {
        this.isLoading = false;
      });
    } catch (error) {
      runInAction(() => {
        this.isLoading = false;
      });
      toastStore.showError('Не удалось отправить код. Проверьте номер телефона.');
      throw error;
    }
  };

  verifyCode = async (phone: string, code: string): Promise<void> => {
    this.isLoading = true;
    try {
      const response = await adminAuthLogin({
        requestBody: {
          phone,
          code,
          panel: AdminSendCodeDto.panel.SUPERADMIN,
        },
      });
      runInAction(() => {
        this.accessToken = response.accessToken;
        this.refreshToken = response.refreshToken;
        this.user = response.user;
        this.isAuthenticated = true;
        this.isLoading = false;
      });
      OpenAPI.TOKEN = response.accessToken;
      localStorage.setItem('accessToken', response.accessToken);
      localStorage.setItem('refreshToken', response.refreshToken);
      localStorage.setItem('user', JSON.stringify(response.user));
    } catch (error) {
      runInAction(() => {
        this.isLoading = false;
      });
      toastStore.showError('Неверный код или истек срок действия.');
      throw error;
    }
  };

  refreshTokens = async (): Promise<void> => {
    if (!this.refreshToken) {
      this.logout();
      return;
    }
    try {
      const response = await axios.post<RefreshResponse>(
        `${API_BASE()}/api/auth/refresh`,
        {},
        { headers: { Authorization: `Bearer ${this.refreshToken}` } }
      );
      runInAction(() => {
        this.accessToken = response.data.accessToken;
        this.refreshToken = response.data.refreshToken;
      });
      OpenAPI.TOKEN = response.data.accessToken;
      localStorage.setItem('accessToken', response.data.accessToken);
      localStorage.setItem('refreshToken', response.data.refreshToken);
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.status === 401) {
        this.logout();
      }
      throw error;
    }
  };

  logout = () => {
    runInAction(() => {
      this.isAuthenticated = false;
      this.accessToken = null;
      this.refreshToken = null;
      this.user = null;
    });
    OpenAPI.TOKEN = undefined;
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('user');
  };
}

export const authStore = new AuthStore();
